import { Link } from 'react-router-dom';
import { getAdvice, type AdviceItem } from '../api.js';
import { EmptyState, ErrorState, Icon, type IconName } from '../ui/index.js';
import { useLoad } from '../lifeos/useLoad.js';
import './reports.css';

/**
 * Advice — a Monarch-style checklist built from his own numbers (the server's
 * deterministic checks): green for what's in good shape, amber for what needs
 * a look, blue for a next step. Anything that needs judgement goes to the coach.
 */

const LEVELS: Record<AdviceItem['level'], { icon: IconName; label: string }> = {
  good: { icon: 'check', label: 'Looking good' },
  warn: { icon: 'alert', label: 'Needs attention' },
  todo: { icon: 'arrow-up', label: 'Next step' },
};

/** Warnings first, then next steps, then what's already fine. */
const ORDER: Record<AdviceItem['level'], number> = { warn: 0, todo: 1, good: 2 };

export default function Advice(): JSX.Element {
  const advice = useLoad(() => getAdvice(), []);
  const items = [...(advice.data ?? [])].sort((a, b) => ORDER[a.level] - ORDER[b.level]);
  const good = items.filter((i) => i.level === 'good').length;

  return (
    <div className="mr-page">
      {advice.error && !advice.data ? (
        <ErrorState message={advice.error} onRetry={() => void advice.reload()} />
      ) : !advice.data ? (
        <div className="mr-loading" aria-busy="true">
          Loading…
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon="bulb" title="Nothing to check yet" hint="Advice appears once a few weeks of transactions have synced." />
      ) : (
        <>
          <p className="mr-advice__score">
            <b>{good}</b> of {items.length} checks in good shape
          </p>
          <ul className="mr-advice" aria-label="Advice">
            {items.map((item) => {
              const lv = LEVELS[item.level];
              return (
                <li key={item.key} className={`mr-card mr-tip mr-tip--${item.level}`}>
                  <span className="mr-tip__icon" role="img" aria-label={lv.label}>
                    <Icon name={lv.icon} size={16} />
                  </span>
                  <span className="mr-tip__text">
                    <span className="mr-tip__title">{item.title}</span>
                    <span className="mr-tip__detail">{item.detail}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      <Link to="/coach" className="mr-card mr-askcoach">
        <span className="mr-tip__icon mr-tip__icon--coach" aria-hidden="true">
          <Icon name="chat" size={18} />
        </span>
        <span className="mr-tip__text">
          <span className="mr-tip__title">Ask the coach about your money</span>
          <span className="mr-tip__detail">It can see your accounts and spending, and talk a plan through with you.</span>
        </span>
        <Icon name="chevron-right" size={18} className="ui-row__chev" />
      </Link>
    </div>
  );
}
